import {
  CHECK_IN_MOOD_GROUP_KEY,
  checkInDataSchema,
  type CheckInContextGroupSummary,
  type CheckInData,
  type CheckInEntry,
} from './schema';

// Pure derivation of a member's recent check-in signal from their
// `activity_events(verb=checked-in)` rows. The check-ins domain owns check-in
// semantics, so the streak rule lives here and the home summary consumes it —
// one definition of "current streak", never two. The free-day-run/lapse rule
// below is the same bargain for a second, different signal: consumers import
// it rather than re-deriving abstinence semantics of their own. Lives in the shared package
// (below every app layer) because repo-layer prompt readers across workspaces
// consume it — a pure derivation must never force a repo to import a service.

/** The structural row shape the derivation reads — any checked-in event row qualifies. */
export interface CheckInEventRow {
  occurredAt: Date | string;
  payload: unknown;
}

export const DAY_MS = 86_400_000;

/** UTC calendar day of a Date, `YYYY-MM-DD`. */
export const isoDay = (d: Date): string => d.toISOString().slice(0, 10);

/** Shift a `YYYY-MM-DD` day by `delta` calendar days (UTC). */
export const shiftDay = (isoDate: string, delta: number): string =>
  new Date(Date.parse(`${isoDate}T00:00:00.000Z`) + delta * DAY_MS).toISOString().slice(0, 10);

/**
 * Consecutive calendar days with any check-in, ending at/near `today`. The run
 * is "current" only while the latest check-in is within two days of `today`
 * (not yet checked in today, or a missed day, still counts); a >=3-day gap
 * resets it to 0, and so does a latest check-in dated after `today`.
 * `datesDesc` is distinct `YYYY-MM-DD`, newest first; `today` must be the same
 * member-local calendar the dates were written in.
 */
export function currentStreak(datesDesc: readonly string[], today: string): number {
  if (datesDesc.length === 0) return 0;
  const gap = Math.round(
    (Date.parse(`${today}T00:00:00.000Z`) - Date.parse(`${datesDesc[0]}T00:00:00.000Z`)) / DAY_MS,
  );
  if (gap < 0 || gap > 2) return 0;
  let streak = 1;
  for (let i = 1; i < datesDesc.length; i += 1) {
    if (datesDesc[i] === shiftDay(datesDesc[i - 1], -1)) streak += 1;
    else break;
  }
  return streak;
}

/**
 * The primary-program entry of a stored check-in: the first one. The write path
 * fixes that — it attributes the day's care plan, the activity's object ref and
 * the published event's program domain to `entries[0]` — so a reader asking
 * "what did the member log for their main program" reads the same entry instead
 * of re-deriving a notion of "primary" that could disagree with what was
 * written. Absent on legacy payloads, which carry no entries at all.
 */
function primaryProgramEntry(payload: CheckInData): CheckInEntry | undefined {
  return payload.entries.at(0);
}

/**
 * Whether one entry records a free day. Both stored fields must agree: `isFree`
 * alongside a logged `quantity` is a contradiction the client should never
 * write, and the day-level rollup the profile calendar colours already derives
 * freeness from summed quantity, so requiring both keeps the two surfaces from
 * reading the same row differently. Applied identically to the judged day and
 * to the days behind it, so no row can count as free in one role and not-free
 * in the other.
 */
function isFreeEntry(entry: CheckInEntry): boolean {
  return entry.isFree && entry.quantity === 0;
}

/** A stored check-in whose primary program was free that day. */
function isFreeDay(payload: CheckInData): boolean {
  const entry = primaryProgramEntry(payload);
  return entry !== undefined && isFreeEntry(entry);
}

export interface LapseArgs {
  /**
   * The check-in being judged, as stored. This is the caller's own row — never
   * a lookup into {@link precedingByDate} — so a correcting append written
   * afterwards cannot re-decide an evaluation of content it never saw.
   */
  checkIn: CheckInData;
  /**
   * Check-ins for the days BEFORE `checkIn.date`, keyed by `YYYY-MM-DD`, and
   * already resolved latest-per-date by the caller: the check-in tables are
   * append-only, so a correcting append supersedes an earlier row for the same
   * date. A `Map` is the parameter shape precisely because it cannot hold two
   * rows for one date — a corrected day is structurally counted once, at its
   * current value, rather than inflating the run it sits in.
   */
  precedingByDate: ReadonlyMap<string, CheckInData>;
  /** How many consecutive free days must precede the judged day. */
  runDays: number;
}

/**
 * Whether a check-in records a lapse: its primary program was not free, after
 * a run of at least `runDays` consecutive free days immediately before it.
 *
 * The run threshold is what makes this a different signal from "the member
 * logged something today". A lapse is a stretch of free days ending, so below
 * the threshold there is no stretch to end and an isolated not-free day is not
 * one — which is also why a `runDays` of 0 or less imposes no run requirement
 * at all and makes every not-free day qualify.
 *
 * Distinct from {@link currentStreak}, which counts consecutive days with ANY
 * check-in — engagement. This counts consecutive FREE days and reports the day
 * that ends them.
 *
 * Pure: the caller owns every read, and the walk backwards is bounded to
 * `runDays` days, so no unbounded history scan can hide behind it.
 */
export function isLapse({ checkIn, precedingByDate, runDays }: LapseArgs): boolean {
  const judged = primaryProgramEntry(checkIn);
  // No primary-program entry means the day records nothing to judge — treating
  // its absence as "not free" would make every legacy payload a lapse.
  if (judged === undefined || isFreeEntry(judged)) return false;

  let run = 0;
  let day = shiftDay(checkIn.date, -1);
  while (run < runDays) {
    const previous = precedingByDate.get(day);
    // A missing day is a gap, not a free day: nothing was recorded, so the run
    // ends there rather than reaching over it.
    if (previous === undefined || !isFreeDay(previous)) break;
    run += 1;
    day = shiftDay(day, -1);
  }
  return run >= runDays;
}

/** An event row whose payload has passed the shared check-in schema. */
export interface ParsedCheckInEvent {
  occurredAt: Date;
  payload: CheckInData;
}

export interface ParseCheckInEventsResult {
  valid: ParsedCheckInEvent[];
  /** Rows whose stored payload failed the schema — excluded from derivation. */
  invalidCount: number;
}

/**
 * Validate raw event rows against the shared check-in schema. The stored
 * payload column is polymorphic (`jsonb`, one shape per verb), so this parse is
 * the boundary where a `checked-in` payload becomes typed — downstream
 * derivation never casts. A malformed row is dropped (never a throw): the
 * caller counts it, emits its own signal, and derives from the valid rest.
 */
export function parseCheckInEvents(rows: readonly CheckInEventRow[]): ParseCheckInEventsResult {
  const valid: ParsedCheckInEvent[] = [];
  let invalidCount = 0;
  for (const row of rows) {
    const parsed = checkInDataSchema.safeParse(row.payload);
    if (parsed.success) valid.push({ occurredAt: new Date(row.occurredAt), payload: parsed.data });
    else invalidCount += 1;
  }
  return { valid, invalidCount };
}

/** Distinct `YYYY-MM-DD` check-in days, oldest first, from parsed event rows. */
export function distinctCheckInDaysAsc(events: readonly ParsedCheckInEvent[]): string[] {
  // Array.from (not a Set spread): this file compiles under every consuming
  // workspace's target, including ones without downlevel iteration.
  return Array.from(new Set(events.map((ev) => ev.payload.date))).sort();
}

/**
 * One rendered-facing tag group from a check-in.
 *
 * Re-exported from `./schema`, not re-declared: the Zod schema there owns this
 * shape (ADR-066, one owner per type dimension) because it also has to describe
 * it on the wire. Re-exported rather than merely imported so the name stays
 * importable from the module whose functions return it, leaving every existing
 * consumer's import path unchanged.
 */
export type { CheckInContextGroupSummary };

export interface CheckInRecency {
  /** Current consecutive-day streak, per {@link currentStreak}. */
  streak: number;
  /** Distinct days with a check-in in the trailing 7 days ending at `today`. */
  weekCount: number;
  /** The most recent check-in day, `YYYY-MM-DD`. */
  lastDate: string;
  /**
   * Moods from the latest check-in: the `mood` tag group (selection order
   * preserved) when present, else the legacy `moodAfter`/`moodBefore` field.
   */
  lastMoods: string[];
  /** The most recent check-in's free-text note, if any (unbounded here). */
  lastNote: string | null;
  /** The most recent check-in's non-mood tag groups, insertion order. */
  lastContextGroups: CheckInContextGroupSummary[];
  /** The most recent check-in's per-program entries (may be empty on legacy shapes). */
  lastEntries: CheckInEntry[];
}

/**
 * The moods a stored check-in payload records, in selection order: the served
 * `mood` tag group when it carries anything, else the legacy
 * `moodAfter`/`moodBefore` scalar as a single-element list, else empty.
 *
 * Exported because it is the ONE definition of that precedence. Every reader
 * that answers "how did the member feel" — the voice prompt context below, the
 * home summary's daily-tracking tile — resolves it through here, so changing
 * the rule can never leave two surfaces disagreeing.
 *
 * SANCTIONED EXCEPTION — one mirror of this rule exists outside TypeScript:
 * the `member_list_check_in_moods(jsonb)` SQL function that maintains
 * `member_list_projections.last_check_in_moods`. The member list is a
 * materialised read model (CQRS), so the value must exist as a column on the
 * projection row; resolving it at read time would mean either a second query
 * per page — the projection exists to remove exactly that — or copying whole
 * check-in payloads, including the member's free-text notes, into a
 * list-serving cache. This function stays the source of truth; the SQL is a
 * mirror, never an alternative.
 *
 * OBLIGATION — if you change the precedence here, you MUST update that SQL
 * function in the same change. A drift-guard test runs the canonical
 * stored-shape fixtures through both implementations and asserts they agree, so
 * a one-sided edit fails CI rather than diverging silently in production.
 */
export function latestMoods(payload: CheckInData): string[] {
  const moodGroup = payload.context?.[CHECK_IN_MOOD_GROUP_KEY] ?? [];
  if (moodGroup.length > 0) return moodGroup;
  const legacy = payload.moodAfter ?? payload.moodBefore;
  return legacy ? [legacy] : [];
}

/**
 * The single event {@link deriveCheckInRecency} treats as "latest" — the one
 * whose payload feeds the mood/note/tags/entries fields. Exported so a caller
 * rendering per-event history alongside the summary can exclude exactly the
 * event the summary consumed (structural identity, never a position
 * assumption about its input ordering). Returns an element of `events`, so
 * reference equality identifies it.
 */
export function latestCheckInEvent(
  events: readonly ParsedCheckInEvent[],
): ParsedCheckInEvent | null {
  if (events.length === 0) return null;
  return [...events].sort((a, b) => a.occurredAt.getTime() - b.occurredAt.getTime())[
    events.length - 1
  ];
}

/**
 * The non-mood tag groups a stored payload records, in the order the client
 * happened to write the record's keys.
 *
 * DELIBERATELY NOT the served group order, and deliberately left that way. Two
 * declared prompt-context feeds read this (ADR-088 Rule 1): the voice
 * member-context block and the reminder-nudge context. The voice one renders it
 * after a `.slice(0, MAX_CONTEXT_GROUPS)` **head**-slice, so changing this order
 * would silently re-order two agent prompts and — once more non-mood groups are
 * served than the cap — change which groups an agent is told about at all.
 *
 * That is a real latent defect with its own ticket, not something to fix in
 * passing from a rendering change: per ADR-088 Rule 2 it needs both readers
 * reconciled in the same change with shared-fixture updates.
 *
 * The home surface needs served order, so it calls {@link orderedContextGroups}
 * instead. The two coexist on purpose — this one answers "what did the member
 * record", that one answers "how should the catalog present it".
 */
function latestContextGroups(payload: CheckInData): CheckInContextGroupSummary[] {
  if (!payload.context) return [];
  return Object.entries(payload.context)
    .filter(([group, tags]) => group !== CHECK_IN_MOOD_GROUP_KEY && tags.length > 0)
    .map(([group, tags]) => ({ group, tags }));
}

/**
 * Every non-empty tag group a stored payload records, in the CATALOG'S SERVED
 * group order, with any group the payload carries that the catalog no longer
 * serves appended after them.
 *
 * `mood` is NOT excluded here. This is the faithful projection of what was
 * stored, ordered the way the catalog serves it; the one consumer that must drop
 * the mood group — the home's "surrounding" card — does so in the shared surface
 * author, where both callers inherit the rule (ADR-015).
 *
 * The retired-group tail is what stops a vocabulary edit from hiding history: a
 * group removed from the registry is simply never visited by the served-order
 * walk, so without the tail a member's recorded tags would vanish from the
 * summary the day the group was retired — while the stored payload still held
 * them. Ordering them last rather than interleaved is the honest answer: the
 * catalog has no opinion on where a group it no longer serves belongs.
 *
 * `servedOrder` is injected rather than read here so this stays a pure function
 * over the payload — the caller owns which registry it means (ADR-022).
 */
export function orderedContextGroups(
  payload: CheckInData,
  servedOrder: readonly string[],
): CheckInContextGroupSummary[] {
  if (!payload.context) return [];
  const stored = payload.context;
  const emitted: CheckInContextGroupSummary[] = [];
  const seen = new Set<string>();

  for (const group of servedOrder) {
    const tags = stored[group];
    seen.add(group);
    if (tags && tags.length > 0) emitted.push({ group, tags });
  }
  for (const [group, tags] of Object.entries(stored)) {
    if (seen.has(group)) continue;
    if (tags.length > 0) emitted.push({ group, tags });
  }

  return emitted;
}

/**
 * Derive the recent-check-in signal, or `null` when the member has never
 * checked in. `today` must be the member-local `YYYY-MM-DD` calendar day: check-in dates are member-local, so a caller that passes the UTC day instead shifts the 7-day window and the streak gap by a day for any member whose local day differs from UTC's. Reads the latest event's
 * payload for mood/note/tags/entries; a same-day re-submit supersedes on read
 * (latest event wins), matching the home summary's latest-wins rule.
 */
export function deriveCheckInRecency(
  events: readonly ParsedCheckInEvent[],
  today: string,
): CheckInRecency | null {
  if (events.length === 0) return null;
  const daysAsc = distinctCheckInDaysAsc(events);
  if (daysAsc.length === 0) return null;

  const weekStart = shiftDay(today, -6);
  const weekCount = daysAsc.filter((d) => d >= weekStart && d <= today).length;

  // Latest event by occurredAt (events arrive oldest-first from the repo).
  const latest = latestCheckInEvent(events);
  if (!latest) return null;
  const payload = latest.payload;

  return {
    streak: currentStreak([...daysAsc].reverse(), today),
    weekCount,
    lastDate: daysAsc[daysAsc.length - 1],
    lastMoods: latestMoods(payload),
    lastNote: payload.notes ?? null,
    lastContextGroups: latestContextGroups(payload),
    lastEntries: payload.entries,
  };
}
